import { forwardRef, InputHTMLAttributes, useState } from 'react';
import { EyeIcon, EyeSlashIcon } from '@phosphor-icons/react';
import { PasswordStrengthIndicator } from './PasswordStrengthIndicator';

interface FormInputProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string | undefined;
  required?: boolean;
  showPasswordStrength?: boolean;
  passwordValue?: string;
}

export const FormInput = forwardRef<HTMLInputElement, FormInputProps>(
  (
    {
      label,
      error,
      required,
      className = '',
      type,
      showPasswordStrength = false,
      passwordValue = '',
      ...props
    },
    ref,
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const isPasswordField = type === 'password';
    const inputType = isPasswordField && showPassword ? 'text' : type;

    return (
      <div className="w-full">
        <label className="block text-sm font-medium text-text mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <div className={`${isPasswordField ? 'relative' : ''}`}>
          <input
            ref={ref}
            type={inputType}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all ${
              error ? 'border-red-500' : 'border-gray-300'
            } ${isPasswordField ? 'pr-12' : ''} ${className}`}
            {...props}
          />
          {isPasswordField && (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
              tabIndex={-1}
            >
              {showPassword ? (
                <EyeSlashIcon size={20} weight="regular" />
              ) : (
                <EyeIcon size={20} weight="regular" />
              )}
            </button>
          )}
        </div>
        {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
        {showPasswordStrength && passwordValue && (
          <PasswordStrengthIndicator password={passwordValue} />
        )}
      </div>
    );
  },
);

FormInput.displayName = 'FormInput';
